import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  CircularProgress,
  Alert,
  Stack,
  Chip,
  Drawer,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemText,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import api from '../../utils/api';
import { useGcWorkflowConfig } from './gcWorkflowConstants';

function contactLabel(c) {
  if (!c) return '';
  const name = `${c.first_name || ''} ${c.last_name || ''}`.trim();
  return `${name} — ${c.email || ''}`;
}

function empName(row) {
  const c = row.contact;
  if (!c) return '—';
  return `${c.first_name || ''} ${c.last_name || ''}`.trim() || '—';
}

const PHASE_LABEL = { perm: 'PERM', i140: 'I-140', i485: 'I-485', done: 'Complete' };

export default function ProcessGCList() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [page, setPage] = useState(1);

  const [openAdd, setOpenAdd] = useState(false);
  const [contactOptions, setContactOptions] = useState([]);
  const [contactLoading, setContactLoading] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [notesNew, setNotesNew] = useState('');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [wfBusy, setWfBusy] = useState(false);

  const [pwdRef, setPwdRef] = useState('');
  const [pwdDate, setPwdDate] = useState('');
  const [dolCase, setDolCase] = useState('');
  const [i140Rec, setI140Rec] = useState('');
  const [i485Rec, setI485Rec] = useState('');
  const [notes, setNotes] = useState('');

  const [uploadSlot, setUploadSlot] = useState('');

  const {
    GC_STEPS,
    GC_UPLOAD_SLOTS,
    GC_STEP_META,
    stepIndex,
    mainPhaseForStep,
  } = useGcWorkflowConfig();

  const loadCases = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/immigration/gc-cases', { params: { page, limit: 25 } });
      setRows(res.data?.data || []);
      const p = res.data?.pagination;
      if (p) setPagination({ page: p.page, totalPages: p.totalPages, total: p.total });
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load GC cases.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    loadCases();
  }, [loadCases]);

  useEffect(() => {
    if (!detail) return;
    const cur = detail.workflow_step;
    const slots = GC_UPLOAD_SLOTS;
    if (cur && slots.includes(cur)) setUploadSlot(cur);
    else if (slots.length) setUploadSlot(slots[0]);
  }, [detail, GC_UPLOAD_SLOTS]);

  const loadEmployees = useCallback(async () => {
    setContactLoading(true);
    try {
      const res = await api.get('/contacts', {
        params: { contact_type: 'employee', status: 'active', limit: 500, page: 1 },
      });
      setContactOptions(res.data?.data || []);
    } catch {
      setContactOptions([]);
    } finally {
      setContactLoading(false);
    }
  }, []);

  const openAddDialog = () => {
    setFormError('');
    setSelectedEmployee(null);
    setNotesNew('');
    setOpenAdd(true);
    loadEmployees();
  };

  const createCase = async () => {
    setFormError('');
    if (!selectedEmployee) {
      setFormError('Select an employee.');
      return;
    }
    setSaving(true);
    try {
      await api.post('/immigration/gc-cases', {
        contact_id: selectedEmployee.id,
        notes: notesNew.trim() || undefined,
      });
      setOpenAdd(false);
      loadCases();
    } catch (e) {
      setFormError(e.response?.data?.message || 'Failed to create case.');
    } finally {
      setSaving(false);
    }
  };

  const hydrateDetail = (d) => {
    setDetail(d);
    setPwdRef(d?.pwd_tracking_reference || '');
    setPwdDate(d?.pwd_determination_date || '');
    setDolCase(d?.dol_perm_case_number || '');
    setI140Rec(d?.i140_uscis_receipt || '');
    setI485Rec(d?.i485_uscis_receipt || '');
    setNotes(d?.notes || '');
    const cur = d?.workflow_step;
    const slots = GC_UPLOAD_SLOTS;
    if (cur && slots.includes(cur)) setUploadSlot(cur);
    else setUploadSlot(slots[0] || '');
  };

  const loadDetail = async (id) => {
    setDetailLoading(true);
    setDetailError('');
    try {
      const res = await api.get(`/immigration/gc-cases/${id}`);
      hydrateDetail(res.data?.data);
    } catch (e) {
      setDetailError(e.response?.data?.message || 'Failed to load case.');
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const openDrawer = (id) => {
    setActiveId(id);
    setDrawerOpen(true);
    loadDetail(id);
  };

  const saveTracking = async () => {
    if (!activeId) return;
    setDetailError('');
    try {
      const res = await api.patch(`/immigration/gc-cases/${activeId}`, {
        pwd_tracking_reference: pwdRef.trim() || null,
        pwd_determination_date: pwdDate || null,
        dol_perm_case_number: dolCase.trim() || null,
        i140_uscis_receipt: i140Rec.trim() || null,
        i485_uscis_receipt: i485Rec.trim() || null,
        notes: notes.trim() || null,
      });
      hydrateDetail(res.data?.data);
      loadCases();
    } catch (e) {
      setDetailError(e.response?.data?.message || 'Save failed.');
    }
  };

  const runGcWorkflow = async (action) => {
    if (!activeId) return;
    setDetailError('');
    setWfBusy(true);
    try {
      const res = await api.patch(`/immigration/gc-cases/${activeId}/workflow`, { action });
      hydrateDetail(res.data?.data);
      loadCases();
    } catch (e) {
      setDetailError(e.response?.data?.message || 'Workflow update failed.');
    } finally {
      setWfBusy(false);
    }
  };

  const uploadFile = async (file) => {
    if (!activeId || !file || !uploadSlot) return;
    setDetailError('');
    const fd = new FormData();
    fd.append('file', file);
    fd.append('slot', uploadSlot);
    try {
      const res = await api.post(`/immigration/gc-cases/${activeId}/attachments`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      hydrateDetail(res.data?.data);
      loadCases();
    } catch (e) {
      setDetailError(e.response?.data?.message || 'Upload failed.');
    }
  };

  const downloadDoc = async (documentId, fileName) => {
    if (!activeId || !documentId) return;
    try {
      const res = await api.get(`/immigration/gc-cases/${activeId}/attachments/${documentId}/download`, {
        responseType: 'blob',
      });
      const blob = res.data instanceof Blob ? res.data : new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName || 'download';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setDetailError('Download failed.');
    }
  };

  const deleteDoc = async (documentId) => {
    if (!activeId || !documentId) return;
    setDetailError('');
    try {
      const res = await api.delete(`/immigration/gc-cases/${activeId}/attachments/${documentId}`);
      hydrateDetail(res.data?.data);
      loadCases();
    } catch (e) {
      setDetailError(e.response?.data?.message || 'Delete failed.');
    }
  };

  const curIdx = detail ? stepIndex(detail.workflow_step) : 0;
  const phase = detail ? mainPhaseForStep(detail.workflow_step) : 'perm';

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
        Process GC
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Track employment-based green card milestones: <strong>PERM</strong> (PWD → recruitment including state job
        bank, Sunday newspapers, online posting, company site, referral posting, notice board → PERM filing → DOL →
        certification), then <strong>I-140</strong>, then <strong>I-485</strong>. Upload PDFs and screenshots to each
        step; use <strong>Back</strong> to return to a prior step when you need corrections or more evidence.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openAddDialog}>
              New GC case
            </Button>
          </Stack>

          {loading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Employee</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Phase</TableCell>
                    <TableCell>Current step</TableCell>
                    <TableCell align="right">Open</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        No GC cases yet.
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((row) => {
                      const ph = mainPhaseForStep(row.workflow_step);
                      const meta = GC_STEP_META[row.workflow_step];
                      return (
                        <TableRow key={row.id} hover>
                          <TableCell>{empName(row)}</TableCell>
                          <TableCell>{row.contact?.email || '—'}</TableCell>
                          <TableCell>
                            <Chip size="small" label={PHASE_LABEL[ph] || ph} variant="outlined" />
                          </TableCell>
                          <TableCell>{meta?.label || row.workflow_step}</TableCell>
                          <TableCell align="right">
                            <Button size="small" variant="outlined" onClick={() => openDrawer(row.id)}>
                              Workflow
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {pagination.totalPages > 1 && (
            <Stack direction="row" spacing={2} sx={{ mt: 2 }} alignItems="center">
              <Button size="small" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                Previous
              </Button>
              <Typography variant="body2">
                Page {page} of {pagination.totalPages} ({pagination.total} total)
              </Typography>
              <Button size="small" disabled={page >= pagination.totalPages} onClick={() => setPage((p) => p + 1)}>
                Next
              </Button>
            </Stack>
          )}
        </CardContent>
      </Card>

      <Dialog open={openAdd} onClose={() => !saving && setOpenAdd(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New GC case</DialogTitle>
        <DialogContent>
          {formError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formError}
            </Alert>
          )}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Autocomplete
              options={contactOptions}
              loading={contactLoading}
              getOptionLabel={(o) => contactLabel(o)}
              value={selectedEmployee}
              onChange={(_, v) => setSelectedEmployee(v)}
              renderInput={(params) => (
                <TextField {...params} label="Employee" size="small" helperText="Employee contacts only." />
              )}
            />
            <TextField label="Notes (optional)" size="small" multiline minRows={2} value={notesNew} onChange={(e) => setNotesNew(e.target.value)} fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAdd(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={createCase} disabled={saving}>
            {saving ? <CircularProgress size={22} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)} PaperProps={{ sx: { width: { xs: '100%', md: 560 } } }}>
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">GC workflow</Typography>
          <IconButton onClick={() => setDrawerOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider />
        <Box sx={{ p: 2, overflow: 'auto', pb: 6 }}>
          {detailLoading && (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          )}
          {detailError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {detailError}
            </Alert>
          )}
          {!detailLoading && detail && (
            <Stack spacing={2}>
              <Typography variant="subtitle2" color="text.secondary">
                {empName({ contact: detail.contact })}
              </Typography>
              <Typography variant="body2">{detail.contact?.email}</Typography>

              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {(['perm', 'i140', 'i485', 'done'] ).map((p) => (
                  <Chip
                    key={p}
                    label={PHASE_LABEL[p]}
                    color={phase === p ? 'primary' : 'default'}
                    variant={phase === p ? 'filled' : 'outlined'}
                  />
                ))}
              </Stack>

              <Alert severity="info" variant="outlined">
                Step {curIdx + 1} of {GC_STEPS.length}: {GC_STEP_META[detail.workflow_step]?.label || detail.workflow_step}
              </Alert>
              <Typography variant="body2" color="text.secondary">
                {GC_STEP_META[detail.workflow_step]?.hint}
              </Typography>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <Button variant="outlined" disabled={wfBusy || curIdx <= 0} onClick={() => runGcWorkflow('go_back')}>
                  Back to previous step
                </Button>
                <Button variant="contained" disabled={wfBusy || detail.workflow_step === 'gc_complete'} onClick={() => runGcWorkflow('advance')}>
                  Complete this step & advance
                </Button>
              </Stack>

              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography fontWeight={600}>Full checklist</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 0 }}>
                  <List dense disablePadding>
                    {GC_STEPS.map((stepKey, idx) => {
                      const done = idx < curIdx;
                      const active = idx === curIdx;
                      const meta = GC_STEP_META[stepKey];
                      return (
                        <ListItem key={stepKey} sx={{ py: 0.25 }}>
                          <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                            {done ? (
                              <CheckCircleIcon fontSize="small" color="success" />
                            ) : active ? (
                              <RadioButtonCheckedIcon fontSize="small" color="primary" />
                            ) : (
                              <RadioButtonUncheckedIcon fontSize="small" color="disabled" />
                            )}
                          </Box>
                          <ListItemText primary={meta?.label || stepKey} secondary={active ? 'Current' : done ? 'Done' : 'Pending'} />
                        </ListItem>
                      );
                    })}
                  </List>
                </AccordionDetails>
              </Accordion>

              <Divider />
              <Typography variant="subtitle2">Tracking fields</Typography>
              <TextField label="PWD / DOL tracking reference" size="small" value={pwdRef} onChange={(e) => setPwdRef(e.target.value)} fullWidth disabled={detail.workflow_step === 'gc_complete'} />
              <TextField label="PWD determination date" type="date" size="small" InputLabelProps={{ shrink: true }} value={pwdDate} onChange={(e) => setPwdDate(e.target.value)} fullWidth disabled={detail.workflow_step === 'gc_complete'} />
              <TextField label="PERM DOL case number" size="small" value={dolCase} onChange={(e) => setDolCase(e.target.value)} fullWidth disabled={detail.workflow_step === 'gc_complete'} />
              <TextField label="I-140 USCIS receipt" size="small" value={i140Rec} onChange={(e) => setI140Rec(e.target.value)} fullWidth disabled={detail.workflow_step === 'gc_complete'} />
              <TextField label="I-485 USCIS receipt" size="small" value={i485Rec} onChange={(e) => setI485Rec(e.target.value)} fullWidth disabled={detail.workflow_step === 'gc_complete'} />
              <TextField label="Notes" size="small" value={notes} onChange={(e) => setNotes(e.target.value)} fullWidth multiline minRows={2} disabled={detail.workflow_step === 'gc_complete'} />
              {detail.workflow_step !== 'gc_complete' && (
                <Button variant="outlined" onClick={saveTracking}>
                  Save tracking
                </Button>
              )}

              <Divider />
              <Typography variant="subtitle2">Upload PDF or screenshot</Typography>
              {detail.workflow_step === 'gc_complete' ? (
                <Typography variant="body2" color="text.secondary">
                  Case complete — uploads locked.
                </Typography>
              ) : (
                <Stack spacing={1.5}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="gc-slot-label">Category (slot)</InputLabel>
                    <Select
                      labelId="gc-slot-label"
                      label="Category (slot)"
                      value={uploadSlot}
                      onChange={(e) => setUploadSlot(e.target.value)}
                    >
                      {GC_UPLOAD_SLOTS.map((slot) => (
                        <MenuItem key={slot} value={slot}>
                          {GC_STEP_META[slot]?.label || slot}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button variant="outlined" component="label">
                    Choose file & upload
                    <input type="file" hidden accept=".pdf,image/*" onChange={(e) => uploadFile(e.target.files?.[0])} />
                  </Button>
                  <Typography variant="caption" color="text.secondary">
                    You can upload to any category you have already reached or are working on; go back a step if you
                    need to add evidence to an earlier recruitment channel.
                  </Typography>
                </Stack>
              )}

              <Divider />
              <Typography variant="subtitle2">Files on record</Typography>
              {GC_UPLOAD_SLOTS.map((slot) => {
                const files = detail.attachments_by_slot?.[slot] || [];
                if (files.length === 0) return null;
                return (
                  <Box key={slot}>
                    <Typography variant="caption" fontWeight={600} display="block" sx={{ mb: 0.5 }}>
                      {GC_STEP_META[slot]?.label || slot}
                    </Typography>
                    <Stack spacing={0.5}>
                      {files.map((f) => (
                        <Stack key={f.id} direction="row" alignItems="center" spacing={1}>
                          <Typography variant="body2" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {f.file_name}
                          </Typography>
                          <IconButton size="small" onClick={() => downloadDoc(f.id, f.file_name)} aria-label="download">
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                          {detail.workflow_step !== 'gc_complete' && (
                            <IconButton size="small" onClick={() => deleteDoc(f.id)} aria-label="delete">
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Stack>
                      ))}
                    </Stack>
                  </Box>
                );
              })}
              {GC_UPLOAD_SLOTS.every((slot) => !(detail.attachments_by_slot?.[slot] || []).length) && (
                <Typography variant="body2" color="text.secondary">
                  No files yet.
                </Typography>
              )}
            </Stack>
          )}
        </Box>
      </Drawer>
    </Box>
  );
}
